/** Canonical path-changing redirects shared by middleware and revenue attribution. */
export const LEGACY_CRAWL_CANONICAL_PATHS: Readonly<Record<string, string>> = {
  "/free-astrology-chat/": "/ai-astrologer/",
  "/membership/": "/ai-astrologer/",
  "/matching/": "/kundli-matching/",
  // These pages already named these winners in their canonical metadata.
  // Resolve the same consolidation in routing and sitemap ownership.
  "/manglik-calculator/": "/mangal-dosha-calculator/",
  "/saturn-transit/": "/shani-transit/",
  // `vivah` was never a governed muhurat event id (the source-of-truth slug is
  // `marriage`) and the generic dynamic fallback earned 0 GSC rows/90d as of
  // 2026-08-04. Consolidate the orphan instead of grandfathering it into the
  // fail-closed dynamic-index registry.
  "/muhurat/vivah/": "/muhurat/marriage/",
  // Consolidated 2026-07-22: thin /prashna/ guide → the /prashna-kundali/ tool
  // (kundli-matching-style cannibalization; the tool page dominates the query).
  "/prashna/": "/prashna-kundali/",
  // `/vastu/` is a stale static artifact. The current hub, locale pair, room
  // directory, schema, and sitemap authority all live under `/vastu-shastra/`.
  "/vastu/": "/vastu-shastra/",
  "/kundali/": "/free-kundali/",
  "/daily-rashifal/": "/rashifal/",
  "/today-rashifal/": "/rashifal/",
  "/pages/about/": "/about/",
  "/pages/about-us/": "/about/",
  "/pages/contact/": "/contact/",
  "/pages/contact-us/": "/contact/",
  "/policies/privacy-policy/": "/privacy/",
  "/policies/refund-policy/": "/refund-policy/",
  "/policies/shipping-policy/": "/shipping-policy/",
  "/policies/terms-of-service/": "/terms/",
  "/rashi/": "/rashi/mesh/",
  "/remedies/": "/upay/",
  "/hi/remedies/": "/hi/upay/",
  "/dosha/kaal-sarp-dosha/": "/kaal-sarp-dosha/",
  "/dosha/mangal-dosha/": "/mangal-dosha-calculator/",
  "/mangal-dosha/": "/mangal-dosha-calculator/",
  "/dosha/pitra-dosha/": "/pitra-dosha/",
  "/dosha/shani-dosha/": "/sade-sati/",
  "/dosha/rahu-dosha/": "/upay/rahu/",
  "/remedies/for-dosha/rahu_dosha/": "/upay/rahu/",
  "/dosha/ketu-dosha/": "/upay/ketu/",
  "/remedies/for-dosha/ketu_dosha/": "/upay/ketu/",
  "/hi/monthly-rashifal/": "/hi/aaj-ka-rashifal/",
  "/hi/muhurat/naamkaran/": "/hi/muhurat/",
  "/collections/frontpage/": "/",
  "/collections/all/": "/upay/",
  "/collections/frontpage.atom": "/",
  "/collections/": "/",
  "/products/": "/",
  // Retired physical-product pages (Shopify catalog era, GSC 404 backlog 2026-07-24).
  // The karungali products map to the surviving karungali editorial page; the rest
  // go home — there is no digital equivalent to inherit their equity.
  //
  // FIXED 2026-08-09: all three pointed at
  // `/blogs/news/karungali-mala-benefits-...`, which does NOT exist and returns
  // 404. A live URL redirecting into a 404 is a soft-404 signal, and it wasted
  // whatever equity these paths still carried — on a domain under an active
  // algorithmic classification (ADR-0091) that is the opposite of what a redirect
  // is for. The page that actually survives is the editorial guide, verified 200.
  //
  // REPAIRED AGAIN 2026-08-09 (ADR-0092): `/products/karungali-mala/` is NO LONGER
  // a redirect — it is the live product route. The redirect was correct only while
  // the product had no page; keeping it after the pilot went live would 301 a
  // ready-to-buy visitor away from the thing they came to buy, into an editorial
  // post. The two legacy duplicate handles consolidate INTO that canonical product
  // page rather than the guide, and the combo handle is a VARIANT, which ADR-0092
  // says is never its own URL — so it resolves to the one product page that owns it.
  "/products/karungali-mala-1/": "/products/karungali-mala/",
  "/products/karungali-mala-siddh-rudraksha-bracelet-combo/": "/products/karungali-mala/",
  "/products/gold-plated-lord-ganesha-idol/": "/",
  "/products/om-pendant/": "/",
  "/products/rudraksha-bracelet-2/": "/",
  "/products/gold-plated-premium-rudraksha-chain-bracelet/": "/",
  "/products/rudraksha-mala-1/": "/",
  "/mahadasha/saturn/": "/mahadasha/shani/",
  "/blogs/news/baby-names-by-nakshatra-complete-guide-for-2026/": "/baby-names/",
  "/blogs/news/sade-sati-2026-which-rashis-are-affected-and-what-to-do/": "/sade-sati/",
  "/blogs/news/free-kundali-how-ai-is-making-vedic-astrology-accessible-t/": "/free-kundali/",
  "/blogs/news/free-kundali-how-ai-is-making-vedic-astrology-accessible-to-everyone/":
    "/free-kundali/",
  "/blogs/news/saturn-transit-in-aries-2026-effects-on-all-12-rashis/": "/shani-transit/",
  "/blogs/news/how-to-find-your-moon-sign-rashi-by-date-of-birth/": "/rashi-calculator/",
  "/blogs/news/kaal-sarp-dosha-signs-effects-and-proven-remedies/": "/kaal-sarp-dosha/",
  "/blogs/news/kundali-matching-beyond-36-gun-milan-what-really-matters/": "/kundli-matching/",
  "/blogs/news/kundli-matching-for-marriage-gun-milan-explained/": "/kundli-matching/",
  "/blogs/news/mangal-dosha-meaning-effects-and-remedies/": "/mangal-dosha-calculator/",
  "/blogs/news/what-is-kundli-complete-guide-to-vedic-birth-chart/": "/free-kundali/",
  "/blogs/news/nakshatra-the-27-lunar-mansions-of-vedic-astrology/": "/nakshatra/",
  "/pages/terms-and-conditions/": "/terms/",
  "/pages/privacy-policy/": "/privacy/",
  "/pages/delete-account/": "/privacy/",
  "/jupiter-transit-2026/": "/jupiter-transit/",
  "/rahu-transit-2026/": "/rahu-transit/",
  "/hi/jupiter-transit-2026/": "/hi/transits/",
  "/hi/rahu-transit-2026/": "/hi/transits/",
  "/kundali-milan/": "/kundli-matching/",
  "/kundli-milan/": "/kundli-matching/",
  "/hi/kundali-milan/": "/hi/kundli-matching/",
  "/hi/kundli-milan/": "/hi/kundli-matching/",
  "/lal-kitab-remedies/": "/lal-kitab/",
  "/hi/": "/hi/aaj-ka-rashifal/",
  // Templated-family removal (owner decision 2026-09-23): an /explain/ page whose
  // entity has its own canonical page 301s there; the rest of /explain/ stays.
  // The rashi and nakshatra explainers are pattern rules in crawl-policy-data.json.
  "/explain/dosha/guru-chandal-dosh/": "/guru-chandal-dosha/",
  "/explain/dosha/kaal-sarp-dosh/": "/kaal-sarp-dosha/",
  "/explain/dosha/kemadruma-yoga/": "/kemdrum-yoga/",
  "/explain/dosha/mangal-dosh/": "/mangal-dosha-calculator/",
  "/explain/dosha/pitra-dosh/": "/pitra-dosha/",
  "/explain/yoga/dhana-yoga/": "/dhana-yoga-calculator/",
  "/explain/yoga/kala-sarpa-yoga/": "/kaal-sarp-dosha/",
  "/explain/yoga/raja-yoga/": "/raj-yoga-calculator/",
  // A year-suffixed festival id whose evergreen twin carries every year's date.
  "/calendar/festival/makar_sankranti_2027/": "/calendar/festival/makar_sankranti/",
  "/hi/calendar/festival/makar_sankranti_2027/": "/hi/calendar/festival/makar_sankranti/",
};
